import type { NavLinkItem } from 'Components/Layout/DrawerMenu/types'
import BasicLayout from 'Components/Layout/Templates/BasicLayout'
import CodespaceRoot from 'Services/ControlDataManagement/Features/Codespace'
import RefundCodeRoot from 'Services/ControlDataManagement/Features/RefundCode'
import ZoneRoot from 'Services/ControlDataManagement/Features/Zone'
import { canAccessControlDataManagement } from 'Services/common/Features/User/userSlice'
import useTypedSelector from 'Store'
import {
  CONTROL_DATA_MANAGEMENT_ROOT_URL,
  IS_HSL_SERVICE_ENVIRONMENT,
  IS_WALTTI_SERVICE_ENVIRONMENT,
  LOGIN_PAGE_PATH
} from 'Util/constants'
import ProtectedRoute from 'Util/ProtectedRoute'
import CodeIcon from '@mui/icons-material/Code'
import PaidIcon from '@mui/icons-material/Paid'
import WebhookIcon from '@mui/icons-material/Webhook'
import { useMemo } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { t } from 'ttag'

const getMenuBarNavLinks = (): NavLinkItem[] => {
  if (IS_HSL_SERVICE_ENVIRONMENT) {
    return [
      {
        title: t`Zones`,
        link: `${CONTROL_DATA_MANAGEMENT_ROOT_URL}/zones`,
        icon: <WebhookIcon fontSize="small" />
      },
      {
        title: t`Refund codes`,
        link: `${CONTROL_DATA_MANAGEMENT_ROOT_URL}/refund-codes`,
        icon: <PaidIcon fontSize="small" />
      },
      {
        title: t`Codespaces`,
        link: `${CONTROL_DATA_MANAGEMENT_ROOT_URL}/codespaces`,
        icon: <CodeIcon fontSize="small" />
      }
    ]
  }

  return [
    {
      title: t`Zones`,
      link: `${CONTROL_DATA_MANAGEMENT_ROOT_URL}/zones`,
      icon: <WebhookIcon fontSize="small" />
    },
    {
      title: t`Codespaces`,
      link: `${CONTROL_DATA_MANAGEMENT_ROOT_URL}/codespaces`,
      icon: <CodeIcon fontSize="small" />
    }
  ]
}

type RouteConfig = {
  isAllowed: boolean
  defaultRedirect: string
  routes: JSX.Element
}

function ControlDataManagement() {
  const { user } = useTypedSelector(state => state.user)

  const routeConfig = useMemo((): RouteConfig => {
    if (IS_HSL_SERVICE_ENVIRONMENT && canAccessControlDataManagement(user)) {
      return {
        isAllowed: true,
        defaultRedirect: 'zones/',
        routes: (
          <>
            <Route path="/zones/*" element={<ZoneRoot />} />
            <Route path="/refund-codes/*" element={<RefundCodeRoot />} />
            <Route path="/codespaces/*" element={<CodespaceRoot />} />
          </>
        )
      }
    }

    if (IS_WALTTI_SERVICE_ENVIRONMENT && canAccessControlDataManagement(user)) {
      return {
        isAllowed: true,
        defaultRedirect: 'zones/',
        routes: (
          <>
            <Route path="/zones/*" element={<ZoneRoot />} />
            <Route path="/codespaces/*" element={<CodespaceRoot />} />
          </>
        )
      }
    }

    return {
      isAllowed: false,
      defaultRedirect: '/',
      routes: <></>
    }
  }, [user])

  return (
    <Routes>
      <Route element={<BasicLayout menuBarNavLinks={getMenuBarNavLinks()} />} path="/">
        <Route
          element={
            <ProtectedRoute
              isAuthenticated={true}
              isAllowed={routeConfig.isAllowed}
              authenticationPath={LOGIN_PAGE_PATH}
              restrictedPath={'/'}
            />
          }
        >
          <Route index element={<Navigate to={routeConfig.defaultRedirect} />} />
          {routeConfig.routes}
        </Route>
      </Route>
    </Routes>
  )
}

export default ControlDataManagement
